import { ExpandLess, ExpandMore, Mail as MailIcon } from '@mui/icons-material';
import {
  FormControlLabel,
  InputLabel,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { postCampaignMessagesReportAction } from 'store/actions/advocacyReports';
import { setSnackbarError, setSnackbarSuccess } from 'store/actions/global';
import {
  BuildButton,
  DatesWrapper,
  FormatLabel,
  OptionsSection,
  OptionsToggle,
  OptionWrapper,
  ReportDescription,
  ReportTitle,
  StyledCard,
  StyledFormControl,
} from '../styles';

const CampaignMessagesReport = ({ campaignUuid, organizationUuid, reportsSchema }) => {
  const dispatch = useDispatch();

  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [format, setFormat] = useState('pdf');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [messagesFilter, setMessagesFilter] = useState('all');

  const handleOptionsToggle = () => setShowOptions(prevState => !prevState);

  const handleBuildReport = () => {
    if (campaignUuid) {
      const payload = {
        campaign: campaignUuid,
        customized_only: messagesFilter === 'customized',
        file_format: format,
      };

      if (startDate) {
        payload.start_date = dayjs(startDate).format('YYYY-MM-DD');
      }

      if (endDate) {
        payload.end_date = dayjs(endDate).format('YYYY-MM-DD');
      }

      dispatch(postCampaignMessagesReportAction(payload, organizationUuid))
        .then(result => {
          dispatch(setSnackbarSuccess({ message: result?.detail }));
        })
        .catch(() => {
          dispatch(
            setSnackbarError({
              message: 'Failed to build report. Make sure all fields are set properly.',
            })
          );
        });

      return;
    }

    if (!selectedCampaign) {
      dispatch(setSnackbarError({ message: 'Please select a campaign' }));

      return;
    }

    const foundCampaign = reportsSchema?.campaigns?.find(
      campaign => campaign.uuid === selectedCampaign
    );

    if (!foundCampaign) return;

    const payload = {
      campaign: foundCampaign.uuid,
      customized_only: messagesFilter === 'customized',
      file_format: format,
    };

    if (startDate) {
      payload.start_date = dayjs(startDate).format('YYYY-MM-DD');
    }

    if (endDate) {
      payload.end_date = dayjs(endDate).format('YYYY-MM-DD');
    }

    dispatch(postCampaignMessagesReportAction(payload, organizationUuid))
      .then(result => {
        dispatch(setSnackbarSuccess({ message: result?.detail }));
      })
      .catch(() => {
        dispatch(
          setSnackbarError({
            message: 'Failed to build report. Make sure all fields are set properly.',
          })
        );
      });
  };

  return (
    <StyledCard variant="outlined">
      <ReportTitle variant="h6">Campaign Messages Report</ReportTitle>
      <ReportDescription variant="body2">
        {campaignUuid
          ? 'Export all messages sent through this campaign.'
          : 'Export all messages sent through a specific campaign.'}
      </ReportDescription>

      {!campaignUuid && (
        <StyledFormControl variant="outlined">
          <InputLabel htmlFor="campaign-native-select" sx={{ backgroundColor: 'white' }}>
            Campaign
          </InputLabel>
          <Select
            variant="outlined"
            native
            value={selectedCampaign}
            onChange={e => setSelectedCampaign(e.target.value)}
            label="Campaign"
            inputProps={{
              id: 'campaign-native-select',
              name: 'campaign',
            }}
          >
            <option aria-label="None" value="" />
            {reportsSchema?.campaigns?.map(campaign => (
              <option key={campaign.uuid} value={campaign.uuid}>
                {campaign.name}
              </option>
            ))}
          </Select>
        </StyledFormControl>
      )}

      <OptionsToggle onClick={handleOptionsToggle}>
        {showOptions ? 'Hide options' : 'Options'}
        {showOptions ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      </OptionsToggle>

      {showOptions && (
        <OptionsSection>
          <OptionWrapper>
            <FormatLabel variant="body2">Format</FormatLabel>
            <RadioGroup row value={format} onChange={e => setFormat(e.target.value.toLowerCase())}>
              <FormControlLabel
                value="pdf"
                control={<Radio size="small" color="primary" />}
                label={<Typography variant="body2">PDF</Typography>}
              />
              <FormControlLabel
                value="csv"
                control={<Radio size="small" color="primary" />}
                label={<Typography variant="body2">CSV</Typography>}
              />
            </RadioGroup>
          </OptionWrapper>

          <OptionWrapper>
            <FormatLabel variant="body2">Messages</FormatLabel>
            <RadioGroup
              row
              value={messagesFilter}
              onChange={e => setMessagesFilter(e.target.value)}
            >
              <FormControlLabel
                value="all"
                control={<Radio size="small" color="primary" />}
                label={<Typography variant="body2">All</Typography>}
              />
              <FormControlLabel
                value="customized"
                control={<Radio size="small" color="primary" />}
                label={<Typography variant="body2">Customized only</Typography>}
              />
            </RadioGroup>
          </OptionWrapper>

          <OptionWrapper>
            <DatesWrapper>
              <TextField
                label="Start date"
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                slotProps={{
                  inputLabel: { shrink: true },
                }}
              />
              <TextField
                label="End date"
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                slotProps={{
                  inputLabel: { shrink: true },
                }}
              />
            </DatesWrapper>
          </OptionWrapper>
        </OptionsSection>
      )}

      <BuildButton
        variant="contained"
        color="primary"
        startIcon={<MailIcon />}
        onClick={handleBuildReport}
      >
        Build Report
      </BuildButton>
    </StyledCard>
  );
};

export default CampaignMessagesReport;

CampaignMessagesReport.defaultProps = {
  campaignUuid: null,
  reportsSchema: null,
};

CampaignMessagesReport.propTypes = {
  campaignUuid: PropTypes.string,
  organizationUuid: PropTypes.string.isRequired,
  reportsSchema: PropTypes.shape({
    campaigns: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        uuid: PropTypes.string,
      })
    ),
  }),
};
